import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import axios from "axios"
import {
  Row,
  Col,
  Card,
  CardBody,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap"
import { Link } from "react-router-dom"
import { getCurrentUser } from "../../../helpers/Utils"
import { isMetaWaDesignDemo } from "../../../helpers/metaWhatsAppDesignDemo"
import { toast } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"

const DUMMY_CONNECTION = {
  display_phone_number: "+91 70155 50472",
  verified_name: "Sofric Ai",
  phone_number: "+91 70155 50472",
  display_name: "Sofric Ai",
  status: "connected",
  messaging_active: true,
  quality_rating: "GREEN",
}

const DUMMY_TEMPLATES = [
  {
    id: "demo-tpl-1",
    name: "order_confirmation",
    language: "en_US",
    category: "UTILITY",
    status: "APPROVED",
    body_preview:
      "Hello {{1}}, your order {{2}} has been confirmed successfully.",
    components: [
      {
        type: "BODY",
        text: "Hello {{1}}, your order {{2}} has been confirmed successfully.",
      },
    ],
    updated_at: new Date().toISOString(),
    _demo: true,
  },
  {
    id: "demo-tpl-2",
    name: "welcome_message",
    language: "en_US",
    category: "MARKETING",
    status: "PENDING",
    body_preview: "Hi {{1}}, welcome to our store! How can we help you today?",
    components: [
      {
        type: "BODY",
        text: "Hi {{1}}, welcome to our store! How can we help you today?",
      },
    ],
    updated_at: new Date(Date.now() - 3600000).toISOString(),
    _demo: true,
  },
  {
    id: "demo-tpl-3",
    name: "payment_reminder",
    language: "hi",
    category: "UTILITY",
    status: "REJECTED",
    body_preview: "Namaste {{1}}, aapka payment {{2}} pending hai.",
    rejection_reason: "Template includes promotional content in Utility",
    components: [
      {
        type: "BODY",
        text: "Namaste {{1}}, aapka payment {{2}} pending hai.",
      },
    ],
    updated_at: new Date(Date.now() - 86400000).toISOString(),
    _demo: true,
  },
  {
    id: "demo-tpl-4",
    name: "shipping_update",
    language: "en_US",
    category: "UTILITY",
    status: "APPROVED",
    body_preview: "Hi {{1}}, your order {{2}} is out for delivery.",
    components: [
      { type: "HEADER", format: "IMAGE" },
      {
        type: "BODY",
        text: "Hi {{1}}, your order {{2}} is out for delivery.",
      },
    ],
    updated_at: new Date(Date.now() - 7200000).toISOString(),
    _demo: true,
  },
]

const DUMMY_CONVERSATIONS = [
  {
    id: "demo-c1",
    contact_name: "Rahul Saini",
    wa_id: "917015550472",
    last_message_at: new Date(Date.now() - 5 * 60000).toISOString(),
    last_message: {
      body: "Sure, please share the invoice.",
      status: "received",
    },
  },
  {
    id: "demo-c2",
    contact_name: "Priya Sharma",
    wa_id: "919876543210",
    last_message_at: new Date(Date.now() - 42 * 60000).toISOString(),
    last_message: {
      body: "Hello Priya, your order ORD-2041 has been confirmed.",
      status: "read",
    },
  },
]

const statusBadgeColor = status => {
  const s = String(status || "").toUpperCase()
  if (s === "APPROVED") return "success"
  if (s === "PENDING") return "warning"
  if (s === "REJECTED") return "danger"
  if (s === "PAUSED" || s === "DISABLED") return "secondary"
  return "light"
}

const FB_SDK_URL = "https://connect.facebook.net/en_US/sdk.js"
const FB_ORIGINS = ["https://www.facebook.com", "https://web.facebook.com"]
/** Wait for WA_EMBEDDED_SIGNUP session after FB.login (~code lasts ~30s) */
const EMBEDDED_SESSION_WAIT_MS = 10000
const EMBEDDED_SESSION_POLL_MS = 250

const authHeaders = () => ({
  "content-type": "application/json",
  Authorization: `Bearer ${
    getCurrentUser()?.token == null ? "" : getCurrentUser().token
  }`,
})

const companyBaseUrl = () =>
  `${process.env.REACT_APP_API_URL}/${localStorage.getItem("company_name")}`

const detectBodyVarCount = tpl => {
  const body = (tpl?.components || []).find(
    c => String(c.type || "").toUpperCase() === "BODY"
  )
  const text = body?.text || tpl?.body_preview || ""
  const matches = String(text).match(/\{\{(\d+)\}\}/g) || []
  if (matches.length === 0) return 0
  return new Set(matches.map(m => m.replace(/[{}]/g, ""))).size
}

const detectHeaderMediaType = tpl => {
  const header = (tpl?.components || []).find(
    c => String(c.type || "").toUpperCase() === "HEADER"
  )
  const format = String(header?.format || "").toUpperCase()
  if (["IMAGE", "VIDEO", "DOCUMENT"].includes(format)) {
    return format.toLowerCase()
  }
  return null
}

/**
 * Load Facebook JS SDK once and resolve when ready.
 */
const loadFacebookSdk = (appId, graphVersion) =>
  new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB)
      return
    }

    const existing = document.getElementById("facebook-jssdk")
    if (existing) {
      const check = setInterval(() => {
        if (window.FB) {
          clearInterval(check)
          resolve(window.FB)
        }
      }, 100)
      setTimeout(() => {
        clearInterval(check)
        reject(new Error("Facebook SDK load timeout"))
      }, 15000)
      return
    }

    window.fbAsyncInit = function () {
      window.FB.init({
        appId,
        cookie: true,
        xfbml: false,
        version: graphVersion || "v21.0",
      })
      resolve(window.FB)
    }

    const script = document.createElement("script")
    script.id = "facebook-jssdk"
    script.src = FB_SDK_URL
    script.async = true
    script.defer = true
    script.onerror = () => reject(new Error("Failed to load Facebook SDK"))
    document.body.appendChild(script)
  })

const MetaWhatsappConnect = () => {
  // Always-on while META_WA_FORCE_DESIGN_DEMO=true (see helper)
  const designDemo = isMetaWaDesignDemo()
  const [loadingConfig, setLoadingConfig] = useState(!designDemo)
  const [connecting, setConnecting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [subscribing, setSubscribing] = useState(false)
  const [sending, setSending] = useState(false)
  const [sendTo, setSendTo] = useState("")
  const [templateName, setTemplateName] = useState(
    designDemo ? DUMMY_TEMPLATES[0].name : ""
  )
  const [languageCode, setLanguageCode] = useState(
    designDemo ? DUMMY_TEMPLATES[0].language : "en_US"
  )
  const [bodyParams, setBodyParams] = useState(
    designDemo ? ["Rahul", "ORD-1001"] : []
  )
  const [headerLink, setHeaderLink] = useState("")
  const [metaConfig, setMetaConfig] = useState(null)
  const [connection, setConnection] = useState(
    designDemo ? DUMMY_CONNECTION : null
  )
  const [webhookUrl, setWebhookUrl] = useState(
    designDemo ? "https://staging.klikcrm.com/webhook/meta-whatsapp" : ""
  )
  const [conversations, setConversations] = useState(
    designDemo ? DUMMY_CONVERSATIONS : []
  )
  const [loadingInbox, setLoadingInbox] = useState(false)
  const [templates, setTemplates] = useState(
    designDemo ? DUMMY_TEMPLATES : []
  )
  const [loadingTemplates, setLoadingTemplates] = useState(false)
  const [syncingTemplates, setSyncingTemplates] = useState(false)
  const [configError, setConfigError] = useState(null)
  const [overviewOpen, setOverviewOpen] = useState(false)
  const [tplSearch, setTplSearch] = useState("")
  const [tplStatusFilter, setTplStatusFilter] = useState("ALL")
  const sessionRef = useRef(null)
  const signupOutcomeRef = useRef(null) // finish | cancel | error | null
  const sdkReadyRef = useRef(false)
  const templatesSectionRef = useRef(null)
  const isAdmin =
    getCurrentUser()?.role_name === "Administrator" ||
    getCurrentUser()?.role_name === "administrator"

  const mapSignupError = (raw, fallback) => {
    const original = String(raw || "").trim()
    const m = original.toLowerCase()
    if (!m) return fallback || "WhatsApp connection failed. Please try again."
    if (m.includes("cancel")) {
      return "WhatsApp connection was cancelled. You can try again anytime."
    }
    if (m.includes("popup") || m.includes("blocked")) {
      return "Facebook login popup was blocked. Please allow popups for this site and try again."
    }
    if (m.includes("timeout") || m.includes("timed out")) {
      return "Connection timed out. Please click Connect WhatsApp again and complete all steps."
    }
    if (
      m.includes("new device") ||
      m.includes("additional checks") ||
      m.includes("feature unavailable") ||
      m.includes("currently unavailable") ||
      m.includes("updating additional details")
    ) {
      return "Facebook could not verify this account/device yet. Use the Facebook account that manages your Business WhatsApp, complete any security checks Facebook shows, then try Connect again from the same browser."
    }
    if (
      m.includes("eligibility") ||
      m.includes("permission") ||
      m.includes("not authorized") ||
      m.includes("onboarding")
    ) {
      return "Your Facebook account could not complete WhatsApp onboarding. Please try again using the Facebook account that manages your business."
    }
    if (m.includes("sdk is not ready") || m.includes("configuration is not ready")) {
      return "WhatsApp connection is not ready yet. Please refresh the page and try again."
    }
    // Prefer real backend message so we can diagnose reconnect issues
    if (original.length <= 220 && !m.includes("sqlstate") && !m.includes("stack")) {
      return original
    }
    return fallback || "WhatsApp connection failed. Please try again."
  }

  const connectChecklist = (
    <div
      className="text-start mx-auto mb-4 p-3 rounded border bg-light"
      style={{ maxWidth: 520 }}
    >
      <div className="fw-semibold mb-2">Before you connect</div>
      <ul className="small text-muted mb-0 ps-3">
        <li>
          Use the <strong>Facebook account that owns / manages</strong> your
          WhatsApp Business number (not a brand-new personal account).
        </li>
        <li>
          Prefer a browser where you already use Facebook Business / Meta
          Business Suite on this device.
        </li>
        <li>
          Allow <strong>popups</strong> for this site, then finish every
          Facebook / WhatsApp step until it closes.
        </li>
        <li>
          If Facebook says “new device” or “Feature unavailable”, complete
          Facebook’s security check first, wait a bit, then try again — this
          is controlled by Meta, not KlikCRM.
        </li>
      </ul>
    </div>
  )

  const selectedTemplate =
    templates.find(
      t => t.name === templateName && t.language === languageCode
    ) || templates.find(t => t.name === templateName) || null

  const headerMediaType = detectHeaderMediaType(selectedTemplate)
  const bodyVarCount = detectBodyVarCount(selectedTemplate)

  const filteredTemplates = useMemo(() => {
    const q = tplSearch.trim().toLowerCase()
    return templates.filter(t => {
      if (
        tplStatusFilter !== "ALL" &&
        String(t.status || "").toUpperCase() !== tplStatusFilter
      ) {
        return false
      }
      if (!q) return true
      return (
        String(t.name || "")
          .toLowerCase()
          .includes(q) ||
        String(t.body_preview || "")
          .toLowerCase()
          .includes(q)
      )
    })
  }, [templates, tplSearch, tplStatusFilter])

  const templateStats = useMemo(() => {
    const stats = { total: templates.length, approved: 0, pending: 0, rejected: 0 }
    templates.forEach(t => {
      const s = String(t.status || "").toUpperCase()
      if (s === "APPROVED") stats.approved += 1
      else if (s === "PENDING") stats.pending += 1
      else if (s === "REJECTED") stats.rejected += 1
    })
    return stats
  }, [templates])

  const scrollToTemplates = () => {
    templatesSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
  }

  const demoToast = msg => {
    toast.info(msg || "Local design preview — action disabled.", {
      position: "top-center",
      autoClose: 3500,
    })
  }

  const applyTemplateSelection = tpl => {
    if (!tpl) {
      setTemplateName("")
      setLanguageCode("en_US")
      setBodyParams([])
      setHeaderLink("")
      return
    }
    setTemplateName(tpl.name)
    setLanguageCode(tpl.language || "en_US")
    setHeaderLink("")
    const count = detectBodyVarCount(tpl)
    setBodyParams(Array(count).fill(""))
  }

  const fetchTemplates = useCallback(() => {
    setLoadingTemplates(true)
    axios
      .get(`${companyBaseUrl()}/whatsapp/meta/templates`, {
        headers: authHeaders(),
      })
      .then(res => {
        setLoadingTemplates(false)
        if (res.data.status) {
          const rows = res.data.data?.templates || []
          setTemplates(Array.isArray(rows) ? rows : [])
          const approved = (Array.isArray(rows) ? rows : []).filter(
            t => String(t.status).toUpperCase() === "APPROVED"
          )
          if (approved.length > 0) {
            setTemplateName(prev => {
              const keep = prev && approved.some(t => t.name === prev)
              const match =
                (keep
                  ? approved.find(t => t.name === prev)
                  : null) || approved[0]
              setLanguageCode(match?.language || "en_US")
              setHeaderLink("")
              setBodyParams(Array(detectBodyVarCount(match)).fill(""))
              return match.name
            })
          }
        } else {
          setTemplates([])
        }
      })
      .catch(() => {
        setLoadingTemplates(false)
        setTemplates([])
      })
  }, [])

  const syncTemplates = () => {
    setSyncingTemplates(true)
    axios
      .post(
        `${companyBaseUrl()}/whatsapp/meta/sync-templates`,
        {},
        { headers: authHeaders() }
      )
      .then(res => {
        setSyncingTemplates(false)
        if (res.data.status) {
          const d = res.data.data || {}
          toast.success(
            res.data.message ||
              `Synced ${d.upserted ?? 0} templates (${d.approved_count ?? 0} approved).`,
            { position: "top-center", autoClose: 5000 }
          )
          fetchTemplates()
        } else {
          toast.error(res.data.message || "Template sync failed.", {
            position: "top-center",
            autoClose: 8000,
          })
        }
      })
      .catch(err => {
        setSyncingTemplates(false)
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.data?.error?.message ||
            "Template sync failed.",
          { position: "top-center", autoClose: 10000 }
        )
      })
  }

  const fetchConversations = useCallback(() => {
    setLoadingInbox(true)
    axios
      .get(`${companyBaseUrl()}/whatsapp/meta/conversations`, {
        headers: authHeaders(),
      })
      .then(res => {
        setLoadingInbox(false)
        if (res.data.status) {
          const rows = res.data.data?.data || res.data.data || []
          setConversations(Array.isArray(rows) ? rows : [])
        }
      })
      .catch(() => {
        setLoadingInbox(false)
        setConversations([])
      })
  }, [])

  const fetchStatus = useCallback(() => {
    return axios
      .get(`${companyBaseUrl()}/whatsapp/meta/status`, {
        headers: authHeaders(),
      })
      .then(res => {
        if (res.data.status && res.data.data?.connected) {
          setConnection(res.data.data.connection)
          if (res.data.data.webhook_url) {
            setWebhookUrl(res.data.data.webhook_url)
          }
          fetchConversations()
          fetchTemplates()
        } else {
          setConnection(null)
          setConversations([])
          setTemplates([])
        }
      })
      .catch(() => {
        setConnection(null)
      })
  }, [fetchConversations, fetchTemplates])

  const fetchConfig = useCallback(() => {
    setLoadingConfig(true)
    setConfigError(null)
    axios
      .get(`${companyBaseUrl()}/whatsapp/meta/config`, {
        headers: authHeaders(),
      })
      .then(async res => {
        if (!res.data.status) {
          setConfigError(res.data.message || "Meta WhatsApp config missing.")
          setLoadingConfig(false)
          return
        }
        const cfg = res.data.data
        setMetaConfig(cfg)
        if (cfg.webhook_url) {
          setWebhookUrl(cfg.webhook_url)
        }
        try {
          await loadFacebookSdk(cfg.app_id, cfg.graph_version)
          sdkReadyRef.current = true
        } catch (err) {
          setConfigError(err.message || "Facebook SDK failed to load.")
        }
        setLoadingConfig(false)
      })
      .catch(err => {
        setConfigError(
          err?.response?.data?.message ||
            "Unable to load Meta WhatsApp configuration."
        )
        setLoadingConfig(false)
      })
  }, [])

  useEffect(() => {
    if (designDemo) {
      // Local design preview — skip live Meta config/status
      setLoadingConfig(false)
      setConnection(DUMMY_CONNECTION)
      setTemplates(DUMMY_TEMPLATES)
      setConversations(DUMMY_CONVERSATIONS)
      setWebhookUrl("https://staging.klikcrm.com/webhook/meta-whatsapp")
      return undefined
    }
    fetchConfig()
    fetchStatus()
    return undefined
  }, [designDemo, fetchConfig, fetchStatus])

  // Embedded Signup posts session info via window message (Meta origins only)
  useEffect(() => {
    const handleMessage = event => {
      if (!FB_ORIGINS.includes(event.origin)) return
      try {
        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data
        if (data?.type !== "WA_EMBEDDED_SIGNUP") return

        const eventName = String(data.event || "")
        if (
          eventName === "FINISH" ||
          eventName === "FINISH_ONLY_WABA" ||
          eventName === "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING" ||
          eventName.startsWith("FINISH")
        ) {
          sessionRef.current = data
          signupOutcomeRef.current = "finish"
        } else if (eventName === "CANCEL") {
          sessionRef.current = null
          signupOutcomeRef.current = "cancel"
        } else if (eventName === "ERROR") {
          sessionRef.current = data
          signupOutcomeRef.current = "error"
        }
      } catch (e) {
        // ignore non-JSON messages
      }
    }
    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [])

  const waitForEmbeddedSession = () =>
    new Promise(resolve => {
      const started = Date.now()
      const tick = () => {
        const outcome = signupOutcomeRef.current
        if (outcome === "cancel") {
          resolve({ session: null, outcome: "cancel" })
          return
        }
        if (outcome === "error") {
          resolve({ session: sessionRef.current, outcome: "error" })
          return
        }

        const session = sessionRef.current
        const hasSessionIds = !!(
          session?.data?.waba_id ||
          session?.waba_id ||
          session?.data?.phone_number_id ||
          session?.phone_number_id
        )

        if (hasSessionIds || outcome === "finish") {
          resolve({ session: session || null, outcome: "finish" })
          return
        }

        if (Date.now() - started >= EMBEDDED_SESSION_WAIT_MS) {
          resolve({ session: session || null, outcome: "timeout" })
          return
        }

        setTimeout(tick, EMBEDDED_SESSION_POLL_MS)
      }
      tick()
    })

  const saveConnection = payload => {
    setConnecting(true)
    axios
      .post(`${companyBaseUrl()}/whatsapp/meta/connect`, payload, {
        headers: authHeaders(),
      })
      .then(res => {
        setConnecting(false)
        if (res.data.status) {
          setConnection(res.data.data.connection)
          if (res.data.data.webhook_url) {
            setWebhookUrl(res.data.data.webhook_url)
          }
          toast.success(res.data.message || "WhatsApp connected successfully.", {
            position: "top-center",
            autoClose: 5000,
          })
          if (res.data.data.webhook_subscribed === false) {
            toast.warning(
              res.data.data.webhook_subscribe_message ||
                "Connected, but message delivery setup needs a quick refresh. Open Account Overview → Refresh webhooks.",
              { position: "top-center", autoClose: 7000 }
            )
          }
          fetchConversations()
          fetchTemplates()
        } else {
          toast.error(
            mapSignupError(
              res.data.message,
              "Failed to save WhatsApp connection."
            ),
            {
              position: "top-center",
              autoClose: 6000,
            }
          )
        }
      })
      .catch(err => {
        setConnecting(false)
        toast.error(
          mapSignupError(
            err?.response?.data?.message,
            "Failed to save WhatsApp connection."
          ),
          {
            position: "top-center",
            autoClose: 6000,
          }
        )
      })
  }

  const launchEmbeddedSignup = async () => {
    if (connecting || disconnecting) return

    if (!metaConfig?.configuration_id) {
      toast.error(
        mapSignupError(
          "configuration",
          "WhatsApp connection is not ready yet. Please refresh and try again."
        ),
        { position: "top-center", autoClose: 5000 }
      )
      return
    }

    if (!window.FB || !sdkReadyRef.current) {
      toast.error(
        mapSignupError(
          "sdk",
          "WhatsApp connection is not ready yet. Please refresh and try again."
        ),
        { position: "top-center", autoClose: 5000 }
      )
      return
    }

    sessionRef.current = null
    signupOutcomeRef.current = null
    setConnecting(true)

    try {
      // FB.login requires a sync function callback — async functions throw
      // "Expression is of type asyncfunction, not function"
      window.FB.login(
        response => {
          if (!response?.authResponse?.code) {
            setConnecting(false)
            const cancelled =
              !response ||
              response.status === "unknown" ||
              response.status === "not_authorized"
            toast.error(
              mapSignupError(
                cancelled ? "cancel" : "onboarding",
                cancelled
                  ? "WhatsApp connection was cancelled. You can try again anytime."
                  : "Your Facebook account could not complete WhatsApp onboarding. Please try again using the Facebook account that manages your business."
              ),
              { position: "top-center", autoClose: 6000 }
            )
            return
          }

          const code = response.authResponse.code
          // Prefer waiting for session IDs; backend can still recover from code alone
          waitForEmbeddedSession()
            .then(({ session, outcome }) => {
              if (outcome === "cancel") {
                setConnecting(false)
                toast.info(
                  mapSignupError("cancel", "WhatsApp connection was cancelled."),
                  { position: "top-center", autoClose: 4500 }
                )
                return
              }

              if (outcome === "error") {
                setConnecting(false)
                toast.error(
                  mapSignupError(
                    "onboarding",
                    "Your Facebook account could not complete WhatsApp onboarding. Please try again using the Facebook account that manages your business."
                  ),
                  { position: "top-center", autoClose: 7000 }
                )
                return
              }

              saveConnection({
                code,
                session: session || null,
                business_id:
                  session?.data?.business_id || session?.business_id || null,
                waba_id: session?.data?.waba_id || session?.waba_id || null,
                phone_number_id:
                  session?.data?.phone_number_id ||
                  session?.phone_number_id ||
                  null,
                reconnect: !!connection,
              })
            })
            .catch(e => {
              setConnecting(false)
              toast.error(
                mapSignupError(
                  e?.message,
                  "WhatsApp connection failed. Please try again."
                ),
                { position: "top-center", autoClose: 6000 }
              )
            })
        },
        {
          config_id: metaConfig.configuration_id,
          response_type: "code",
          override_default_response_type: true,
          extras: {
            setup: {},
            // Empty = standard Cloud API Embedded Signup (not Coexistence / Business App)
            featureType: "whatsapp_business_app_onboarding",
            sessionInfoVersion: "3",
          },
        }
      )
    } catch (e) {
      setConnecting(false)
      toast.error(
        mapSignupError(
          e?.message?.includes("popup") ? "popup" : e?.message,
          "WhatsApp connection failed. Please try again."
        ),
        { position: "top-center", autoClose: 6000 }
      )
    }
  }

  const disconnect = () => {
    setDisconnecting(true)
    axios
      .post(
        `${companyBaseUrl()}/whatsapp/meta/disconnect`,
        {},
        { headers: authHeaders() }
      )
      .then(res => {
        setDisconnecting(false)
        if (res.data.status) {
          setConnection(null)
          setConversations([])
          toast.success(res.data.message || "WhatsApp disconnected.", {
            position: "top-center",
            autoClose: 5000,
          })
        } else {
          toast.error(res.data.message || "Disconnect failed.", {
            position: "top-center",
            autoClose: 5000,
          })
        }
      })
      .catch(err => {
        setDisconnecting(false)
        toast.error(
          err?.response?.data?.message || "Disconnect failed.",
          {
            position: "top-center",
            autoClose: 5000,
          }
        )
      })
  }

  const subscribeWebhooks = () => {
    setSubscribing(true)
    axios
      .post(
        `${companyBaseUrl()}/whatsapp/meta/subscribe-webhooks`,
        {},
        { headers: authHeaders() }
      )
      .then(res => {
        setSubscribing(false)
        if (res.data.status) {
          if (res.data.data?.webhook_url) {
            setWebhookUrl(res.data.data.webhook_url)
          }
          toast.success(res.data.message || "Webhooks subscribed.", {
            position: "top-center",
            autoClose: 5000,
          })
        } else {
          toast.error(res.data.message || "Subscribe failed.", {
            position: "top-center",
            autoClose: 5000,
          })
        }
      })
      .catch(err => {
        setSubscribing(false)
        toast.error(
          err?.response?.data?.message || "Subscribe failed.",
          {
            position: "top-center",
            autoClose: 5000,
          }
        )
      })
  }

  const sendMetaMessage = () => {
    if (!sendTo) {
      toast.error("Recipient number is required.", {
        position: "top-center",
        autoClose: 4000,
      })
      return
    }
    if (!templateName) {
      toast.error("Template name is required.", {
        position: "top-center",
        autoClose: 4000,
      })
      return
    }
    if (headerMediaType && !headerLink.trim()) {
      toast.error(
        `This template requires a ${headerMediaType} header URL.`,
        { position: "top-center", autoClose: 5000 }
      )
      return
    }
    if (bodyVarCount > 0) {
      const missing = bodyParams.some(v => !String(v || "").trim())
      if (missing) {
        toast.error(
          `Fill all ${bodyVarCount} template variable(s) before sending.`,
          { position: "top-center", autoClose: 5000 }
        )
        return
      }
    }

    const payload = {
      to: sendTo,
      type: "template",
      template_name: templateName,
      language_code: languageCode || "en_US",
    }
    if (headerMediaType && headerLink.trim()) {
      payload.header_type = headerMediaType
      payload.header_link = headerLink.trim()
    }
    if (bodyVarCount > 0) {
      payload.body_params = bodyParams.map(v => String(v).trim())
    }

    setSending(true)
    axios
      .post(`${companyBaseUrl()}/whatsapp/meta/send-message`, payload, {
        headers: authHeaders(),
      })
      .then(res => {
        setSending(false)
        if (res.data.status) {
          toast.success(res.data.message || "Message sent.", {
            position: "top-center",
            autoClose: 5000,
          })
          // Delivery can still fail later via webhook — refresh inbox shortly
          fetchConversations()
          setTimeout(() => fetchConversations(), 4000)
          setTimeout(() => fetchConversations(), 12000)
        } else {
          toast.error(res.data.message || "Send failed.", {
            position: "top-center",
            autoClose: 6000,
          })
        }
      })
      .catch(err => {
        setSending(false)
        const apiMsg =
          err?.response?.data?.message ||
          err?.response?.data?.error?.message ||
          (typeof err?.response?.data === "string"
            ? err.response.data.slice(0, 180)
            : null)
        toast.error(
          apiMsg || `Send failed (${err?.response?.status || "network"})`,
          {
            position: "top-center",
            autoClose: 9000,
          }
        )
      })
  }

  const registerPhone = () => {
    setSubscribing(true)
    axios
      .post(
        `${companyBaseUrl()}/whatsapp/meta/register-phone`,
        { pin: "123456" },
        { headers: authHeaders() }
      )
      .then(res => {
        setSubscribing(false)
        if (res.data.status) {
          toast.success(
            res.data.message || "Phone number registered successfully.",
            { position: "top-center", autoClose: 5000 }
          )
        } else {
          toast.error(
            res.data.message ||
              res.data?.data?.error?.message ||
              "Registration failed.",
            { position: "top-center", autoClose: 10000 }
          )
        }
      })
      .catch(err => {
        setSubscribing(false)
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.data?.error?.message ||
            err?.response?.data?.error?.message ||
            "Registration failed.",
          { position: "top-center", autoClose: 12000 }
        )
      })
  }

  const busy = connecting || disconnecting || subscribing

  const onReconnect = () => {
    if (designDemo) {
      demoToast("Local design preview — Reconnect disabled.")
      return
    }
    launchEmbeddedSignup()
  }

  const onDisconnect = () => {
    if (designDemo) {
      demoToast("Local design preview — Disconnect disabled.")
      return
    }
    disconnect()
  }

  const onSyncTemplates = () => {
    if (designDemo) {
      demoToast("Local design preview — Sync disabled.")
      return
    }
    syncTemplates()
  }

  const onSubscribeWebhooks = () => {
    if (designDemo) {
      demoToast("Local design preview — Webhook refresh disabled.")
      return
    }
    subscribeWebhooks()
  }

  const onRegisterPhone = () => {
    if (designDemo) {
      demoToast("Local design preview — Register phone disabled.")
      return
    }
    registerPhone()
  }

  const onSendMetaMessage = () => {
    if (designDemo) {
      demoToast("Local design preview — Send disabled.")
      return
    }
    sendMetaMessage()
  }

  const [actionsMount, setActionsMount] = useState(null)

  useEffect(() => {
    setActionsMount(document.getElementById("meta-wa-connect-actions"))
  }, [])

  const headerActions = connection ? (
    <>
      <button
        type="button"
        className="btn btn-sm btn-outline-primary"
        onClick={scrollToTemplates}
      >
        Manage Templates
      </button>
      <Link
        to="/whatsapp-meta-inbox"
        className="btn btn-sm btn-outline-success"
      >
        Open Message Inbox
      </Link>
      <button
        type="button"
        className="btn btn-sm btn-outline-dark"
        onClick={() => setOverviewOpen(true)}
      >
        Account Overview
      </button>
      <button
        type="button"
        className="btn btn-sm btn-success"
        onClick={onReconnect}
        disabled={busy}
      >
        {connecting ? "Connecting…" : "Reconnect"}
      </button>
      <button
        type="button"
        className="btn btn-sm btn-outline-danger"
        onClick={onDisconnect}
        disabled={busy}
      >
        {disconnecting ? "Disconnecting…" : "Disconnect"}
      </button>
    </>
  ) : (
    <button
      type="button"
      className="btn btn-sm btn-success"
      onClick={launchEmbeddedSignup}
      disabled={busy || loadingConfig || !!configError}
    >
      {connecting ? "Connecting…" : "Connect WhatsApp"}
    </button>
  )

  return (
    <div className="meta-wa-connect">
      {actionsMount ? createPortal(headerActions, actionsMount) : null}

      {designDemo ? (
        <div
          className="px-3 py-2 small mb-3 border"
          style={{
            background: "#fff8e6",
            color: "#7a5b00",
            borderRadius: 8,
          }}
        >
          Local design preview (forced) — dummy connection &amp; templates. Set
          META_WA_FORCE_DESIGN_DEMO=false in helpers/metaWhatsAppDesignDemo.js
          before staging deploy. Live Meta actions are disabled.
        </div>
      ) : null}

      {connection ? (
        <p className="text-muted small mb-3">
          Business number:{" "}
          <strong>
            {connection.display_phone_number ||
              connection.phone_number ||
              "—"}
          </strong>
          {connection.verified_name || connection.display_name
            ? ` · ${connection.verified_name || connection.display_name}`
            : ""}
          {" · "}
          <Badge color="success" pill>
            Connected
          </Badge>
          {connection.messaging_active !== false ? (
            <span className="ms-1">
              <Badge color="info" pill>
                Messaging active
              </Badge>
            </span>
          ) : null}
        </p>
      ) : null}

      {loadingConfig ? (
        <Card className="border-0 shadow-sm">
          <CardBody className="text-center py-5">
            <Spinner color="primary" />
            <p className="text-muted mt-3 mb-0">Loading configuration…</p>
          </CardBody>
        </Card>
      ) : configError && !designDemo ? (
        <Card className="border-0 shadow-sm">
          <CardBody className="py-4">
            <div className="alert alert-warning mb-0">
              <h5 className="alert-heading">Configuration required</h5>
              <p className="mb-0">{configError}</p>
            </div>
          </CardBody>
        </Card>
      ) : !connection ? (
        <Card className="border-0 shadow-sm">
          <CardBody className="text-center py-5 px-4">
            <div
              className="bg-success bg-opacity-10 d-inline-flex align-items-center justify-content-center mb-3"
              style={{ width: 64, height: 64, borderRadius: "50%" }}
            >
              <i className="fab fa-whatsapp text-success fa-2x" />
            </div>
            <h5 className="mb-2">Connect WhatsApp Business</h5>
            <p className="text-muted mx-auto mb-3" style={{ maxWidth: 480 }}>
              Connect your WhatsApp Business account to send and receive
              messages from KlikCRM. You will sign in with Facebook and complete
              a short WhatsApp setup.
            </p>
            {connectChecklist}
            <button
              type="button"
              className="btn btn-success"
              onClick={launchEmbeddedSignup}
              disabled={busy}
            >
              {connecting ? "Connecting…" : "Connect WhatsApp"}
            </button>
          </CardBody>
        </Card>
      ) : (
        <>
          {/* Templates (replaces Account overview + Quick links) */}
          <div ref={templatesSectionRef} className="mb-3">
            <Row className="g-3 mb-3">
              {[
                { label: "Total", value: templateStats.total, color: "#405189" },
                {
                  label: "Approved",
                  value: templateStats.approved,
                  color: "#0ab39c",
                },
                {
                  label: "Pending",
                  value: templateStats.pending,
                  color: "#f7b84b",
                },
                {
                  label: "Rejected",
                  value: templateStats.rejected,
                  color: "#f06548",
                },
              ].map(item => (
                <Col md={3} sm={6} key={item.label}>
                  <Card className="border-0 shadow-sm h-100 mb-0">
                    <CardBody className="py-3">
                      <div className="text-muted small text-uppercase">
                        {item.label}
                      </div>
                      <div
                        className="fs-3 fw-semibold"
                        style={{ color: item.color }}
                      >
                        {item.value}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>

            <Card className="border-0 shadow-sm">
              <CardBody>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                  <div>
                    <h5 className="mb-1">Message templates</h5>
                    <p className="text-muted small mb-0">
                      Create, review, and sync templates from this page.
                    </p>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={onSyncTemplates}
                      disabled={syncingTemplates || busy}
                    >
                      {syncingTemplates ? "Syncing…" : "Sync from Meta"}
                    </button>
                    <Link
                      to="/whatsapp-meta-templates"
                      className="btn btn-success btn-sm"
                      onClick={e => {
                        if (designDemo) {
                          e.preventDefault()
                          demoToast(
                            "Local design preview — open Templates page when ready."
                          )
                        }
                      }}
                    >
                      Create template
                    </Link>
                  </div>
                </div>

                <Row className="g-2 mb-3">
                  <Col md={5}>
                    <input
                      type="search"
                      className="form-control form-control-sm"
                      placeholder="Search templates…"
                      value={tplSearch}
                      onChange={e => setTplSearch(e.target.value)}
                    />
                  </Col>
                  <Col md={3}>
                    <select
                      className="form-select form-select-sm"
                      value={tplStatusFilter}
                      onChange={e => setTplStatusFilter(e.target.value)}
                    >
                      <option value="ALL">All statuses</option>
                      <option value="APPROVED">Approved</option>
                      <option value="PENDING">Pending</option>
                      <option value="REJECTED">Rejected</option>
                    </select>
                  </Col>
                  <Col md={2}>
                    <button
                      type="button"
                      className="btn btn-light btn-sm w-100"
                      onClick={() => {
                        if (designDemo) {
                          setTemplates(DUMMY_TEMPLATES)
                          return
                        }
                        fetchTemplates()
                      }}
                      disabled={loadingTemplates}
                    >
                      {loadingTemplates ? "Loading…" : "Refresh"}
                    </button>
                  </Col>
                </Row>

                {loadingTemplates && templates.length === 0 ? (
                  <div className="text-center py-4">
                    <Spinner size="sm" color="primary" />
                  </div>
                ) : filteredTemplates.length === 0 ? (
                  <div
                    className="text-center text-muted py-4 border bg-light"
                    style={{ borderRadius: 8 }}
                  >
                    No templates found. Sync from Meta or create a new one.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Template</th>
                          <th>Language</th>
                          <th>Category</th>
                          <th>Status</th>
                          <th>Preview</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTemplates.map(t => (
                          <tr key={t.id}>
                            <td className="fw-semibold">{t.name}</td>
                            <td className="small">{t.language}</td>
                            <td>
                              <Badge color="light" className="text-dark">
                                {t.category || "—"}
                              </Badge>
                            </td>
                            <td>
                              <Badge
                                color={statusBadgeColor(t.status)}
                                pill
                              >
                                {t.status || "—"}
                              </Badge>
                              {String(t.status).toUpperCase() === "REJECTED" &&
                              t.rejection_reason ? (
                                <div className="small text-danger mt-1">
                                  {t.rejection_reason}
                                </div>
                              ) : null}
                            </td>
                            <td
                              className="small text-muted"
                              style={{ maxWidth: 280 }}
                            >
                              <div className="text-truncate">
                                {t.body_preview || "—"}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardBody>
            </Card>
          </div>

          {/* Test send */}
          <Card className="border-0 shadow-sm mb-3">
            <CardBody>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <div>
                  <h5 className="mb-1">Send test message</h5>
                  <p className="text-muted small mb-0">
                    Send an approved template. If the template has variables or a
                    media header, fill those fields below.
                  </p>
                </div>
              </div>
              <Row className="g-2 align-items-end">
                <Col md={3}>
                  <label className="form-label">Recipient</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="9198xxxxxxxx"
                    value={sendTo}
                    onChange={e => setSendTo(e.target.value)}
                  />
                </Col>
                <Col md={5}>
                  <label className="form-label">Template</label>
                  {templates.filter(
                    t => String(t.status).toUpperCase() === "APPROVED"
                  ).length > 0 ? (
                    <select
                      className="form-select"
                      value={`${templateName}||${languageCode}`}
                      onChange={e => {
                        const [name, lang] = e.target.value.split("||")
                        const approved = templates.filter(
                          t => String(t.status).toUpperCase() === "APPROVED"
                        )
                        const tpl =
                          approved.find(
                            t => t.name === name && t.language === lang
                          ) || approved.find(t => t.name === name)
                        applyTemplateSelection(tpl)
                      }}
                    >
                      {templates
                        .filter(
                          t => String(t.status).toUpperCase() === "APPROVED"
                        )
                        .map(t => (
                          <option
                            key={`${t.id}-${t.name}-${t.language}`}
                            value={`${t.name}||${t.language}`}
                          >
                            {t.name} ({t.language})
                            {detectBodyVarCount(t) > 0
                              ? ` · ${detectBodyVarCount(t)} vars`
                              : ""}
                          </option>
                        ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      value={templateName}
                      onChange={e => setTemplateName(e.target.value)}
                      placeholder="No approved templates — sync or create first"
                    />
                  )}
                </Col>
                <Col md={2}>
                  <label className="form-label">Language</label>
                  <input
                    type="text"
                    className="form-control"
                    value={languageCode}
                    onChange={e => setLanguageCode(e.target.value)}
                    readOnly={templates.length > 0}
                  />
                </Col>
                <Col md={2}>
                  <button
                    type="button"
                    className="btn btn-primary w-100"
                    onClick={onSendMetaMessage}
                    disabled={sending || busy || !templateName}
                  >
                    {sending ? "Sending…" : "Send"}
                  </button>
                </Col>
              </Row>

              {selectedTemplate?.body_preview ? (
                <p className="small text-muted mt-2 mb-0">
                  Preview: {selectedTemplate.body_preview}
                </p>
              ) : null}

              {headerMediaType ? (
                <Row className="g-2 mt-2">
                  <Col md={8}>
                    <label className="form-label">
                      Header {headerMediaType} URL *
                    </label>
                    <input
                      type="url"
                      className="form-control"
                      value={headerLink}
                      onChange={e => setHeaderLink(e.target.value)}
                      placeholder={`https://… public ${headerMediaType} URL`}
                    />
                  </Col>
                </Row>
              ) : null}

              {bodyVarCount > 0 ? (
                <Row className="g-2 mt-2">
                  {bodyParams.map((val, i) => (
                    <Col md={4} key={i}>
                      <label className="form-label">
                        Variable {"{{"}
                        {i + 1}
                        {"}}"} *
                      </label>
                      <input
                        className="form-control"
                        value={val}
                        onChange={e => {
                          const next = [...bodyParams]
                          next[i] = e.target.value
                          setBodyParams(next)
                        }}
                        placeholder={`Value for {{${i + 1}}}`}
                      />
                    </Col>
                  ))}
                </Row>
              ) : null}
            </CardBody>
          </Card>

          {/* Recent conversations */}
          <Card className="border-0 shadow-sm">
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h5 className="mb-0">Recent conversations</h5>
                  <p className="text-muted small mb-0">
                    Inbound messages received via WhatsApp webhook.
                  </p>
                </div>
                <div className="d-flex gap-2">
                  <Link
                    to="/whatsapp-meta-inbox"
                    className="btn btn-sm btn-outline-success"
                  >
                    Open inbox
                  </Link>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    onClick={() => {
                      if (designDemo) {
                        setConversations(DUMMY_CONVERSATIONS)
                        return
                      }
                      fetchConversations()
                    }}
                    disabled={loadingInbox}
                  >
                    {loadingInbox ? "Loading…" : "Refresh"}
                  </button>
                </div>
              </div>
              {conversations.length === 0 ? (
                <div
                  className="text-center text-muted py-4 border bg-light"
                  style={{ borderRadius: 8 }}
                >
                  No conversations yet. Messages will appear here once customers
                  reply on WhatsApp.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Contact</th>
                        <th>WhatsApp ID</th>
                        <th>Last message</th>
                        <th>Delivery</th>
                        <th>Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conversations.map(c => {
                        const last = c.last_message || c.messages?.[0]
                        const status = String(last?.status || "").toLowerCase()
                        const statusColor =
                          status === "failed"
                            ? "danger"
                            : status === "read"
                            ? "primary"
                            : status === "delivered"
                            ? "success"
                            : status === "sent"
                            ? "secondary"
                            : "light"
                        return (
                          <tr key={c.id}>
                            <td className="fw-medium">
                              {c.contact_name || "Unknown"}
                            </td>
                            <td className="font-monospace small">{c.wa_id}</td>
                            <td>
                              <div>{last?.body || last?.type || "—"}</div>
                              {status === "failed" &&
                              (last?.error_message || last?.error_code) ? (
                                <div className="small text-danger mt-1">
                                  {last.error_message ||
                                    `(#${last.error_code}) Delivery failed`}
                                </div>
                              ) : null}
                            </td>
                            <td>
                              {status ? (
                                <Badge color={statusColor} pill>
                                  {status}
                                </Badge>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="text-muted small">
                              {c.last_message_at
                                ? new Date(c.last_message_at).toLocaleString()
                                : "—"}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Account overview modal */}
          <Modal
            isOpen={overviewOpen}
            toggle={() => setOverviewOpen(false)}
            size="lg"
          >
            <ModalHeader toggle={() => setOverviewOpen(false)}>
              Account overview
            </ModalHeader>
            <ModalBody>
              <Row>
                <Col sm={6} className="mb-3">
                  <div className="text-muted small text-uppercase mb-1">
                    Business number
                  </div>
                  <div className="fs-5 fw-semibold">
                    {connection.display_phone_number ||
                      connection.phone_number ||
                      "—"}
                  </div>
                  {connection.verified_name || connection.display_name ? (
                    <div className="text-muted small">
                      {connection.verified_name || connection.display_name}
                    </div>
                  ) : null}
                </Col>
                <Col sm={6} className="mb-3">
                  <div className="text-muted small text-uppercase mb-1">
                    Status
                  </div>
                  <Badge color="success" pill className="fs-6 me-1">
                    Connected
                  </Badge>
                  <Badge color="info" pill className="fs-6">
                    Messaging active
                  </Badge>
                </Col>
              </Row>
              <hr />
              <div className="d-flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={onSubscribeWebhooks}
                  disabled={busy}
                >
                  {subscribing ? "Refreshing…" : "Refresh messaging"}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={onRegisterPhone}
                  disabled={busy}
                >
                  Register phone
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={onSyncTemplates}
                  disabled={syncingTemplates || busy}
                >
                  {syncingTemplates ? "Syncing…" : "Sync templates"}
                </button>
              </div>
              {isAdmin ? (
                <details className="mt-2">
                  <summary
                    className="text-muted small"
                    style={{ cursor: "pointer" }}
                  >
                    Connection details (admin)
                  </summary>
                  <Row className="small mt-2">
                    <Col md={6} className="mb-2">
                      <span className="text-muted">Business ID</span>
                      <div className="font-monospace">
                        {connection.business_id || "—"}
                      </div>
                    </Col>
                    <Col md={6} className="mb-2">
                      <span className="text-muted">WABA ID</span>
                      <div className="font-monospace">
                        {connection.waba_id || "—"}
                      </div>
                    </Col>
                    <Col md={6} className="mb-2">
                      <span className="text-muted">Phone Number ID</span>
                      <div className="font-monospace">
                        {connection.phone_number_id || "—"}
                      </div>
                    </Col>
                    {(webhookUrl || metaConfig?.webhook_url) && (
                      <Col md={12} className="mb-1">
                        <span className="text-muted">Webhook URL</span>
                        <div className="font-monospace text-break">
                          {webhookUrl || metaConfig.webhook_url}
                        </div>
                      </Col>
                    )}
                  </Row>
                </details>
              ) : null}
            </ModalBody>
            <ModalFooter>
              <button
                type="button"
                className="btn btn-light"
                onClick={() => setOverviewOpen(false)}
              >
                Close
              </button>
            </ModalFooter>
          </Modal>
        </>
      )}
    </div>
  )
}

export default MetaWhatsappConnect